import React from 'react';
import OnePageNavbar from '@/components/OnePageNavbar';
import Footer from '@/components/Footer';
import ContactForm from '@/components/ContactForm';
import ContactInfo from '@/components/ContactInfo';
import TestimonialsCarousel from '@/components/TestimonialsCarousel';
import { Phone, ArrowRight, Plane, Briefcase, PartyPopper, Heart, Clock, Globe, Check, Star, Quote } from 'lucide-react';
import { contactInfo, services, testimonials } from '@/mock';

const OnePageHome = () => {
  const values = [
    {
      title: 'Discrétion',
      description: 'Confidentialité absolue. Vos trajets et échanges restent entre nous.'
    },
    {
      title: 'Ponctualité',
      description: "Toujours à l'heure, avec la sérénité d'un service parfaitement orchestré."
    },
    {
      title: 'Élégance',
      description: 'Un véhicule impeccable et une attention féminine à chaque détail.'
    }
  ];

  const icons = [
    { id: 'plane', icon: <Plane className="w-8 h-8" /> },
    { id: 'briefcase', icon: <Briefcase className="w-8 h-8" /> },
    { id: 'party', icon: <PartyPopper className="w-8 h-8" /> },
    { id: 'heart', icon: <Heart className="w-8 h-8" /> },
    { id: 'clock', icon: <Clock className="w-8 h-8" /> },
    { id: 'globe', icon: <Globe className="w-8 h-8" /> }
  ];

  return (
    <div className="bg-gradient-to-b from-[#2a1a1f] via-[#1a1014] to-[#0f0a0d]">
      <OnePageNavbar />

      {/* Hero Section - ACCUEIL */}
      <section id="accueil" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/hero-sunset.jpeg)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto pt-20">
          <div className="inline-block px-6 py-2 mb-8 rounded-full border border-rose-200/30 bg-black/30 backdrop-blur-sm">
            <span className="text-rose-200 text-sm tracking-wider">⚜ CHAUFFEUR PRIVÉ VTC</span>
          </div>
          
          <h1 className="font-script text-6xl sm:text-7xl lg:text-8xl text-rose-200 mb-6 animate-fade-in">
            LadyGo
          </h1>
          
          <p className="text-2xl sm:text-3xl text-gray-200 mb-8 italic font-light">
            — L'art du transport au féminin —
          </p>
          
          <p className="text-gray-300 text-xl max-w-2xl mx-auto mb-12 leading-relaxed text-center">
            Un service de chauffeur privé pensé pour votre confort, votre sécurité et votre tranquillité. 
            Élégance, écoute et discrétion à chaque trajet.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={`tel:${contactInfo.phoneFormatted}`}
              className="group flex items-center space-x-3 px-8 py-4 rounded-full bg-rose-200 text-gray-900 hover:bg-rose-300 transition-all duration-300 transform hover:scale-105"
            >
              <Phone className="w-5 h-5" />
              <span className="font-medium">{contactInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0f0a0d]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-rose-200 text-sm tracking-widest mb-4">L'ESSENCE LADYGO</p>
            <h2 className="text-4xl sm:text-5xl font-light text-white">
              Un voyage, <span className="font-script text-rose-200">une signature</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={`value-${index}-${value.title}`}
                className="group p-8 rounded-2xl bg-gradient-to-br from-rose-900/10 to-transparent border border-rose-900/20 hover:border-rose-200/40 transition-all duration-300 hover:transform hover:scale-105"
              >
                <h3 className="text-2xl font-light text-rose-200 mb-4">{value.title}</h3>
                <p className="text-gray-400 leading-relaxed text-lg">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#0f0a0d] to-[#1a1014]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-light text-white mb-6">
            Une exigence, <span className="font-script text-rose-200">un standard.</span>
          </h2>
          <p className="text-gray-300 text-xl mb-10 leading-relaxed text-center">
            Transfert aéroport, rendez-vous d'affaires, soirée ou événement : 
            LadyGo transforme chaque déplacement en un moment privilégié.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0f0a0d]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-rose-200 text-sm tracking-widest mb-4">NOS PRESTATIONS</p>
            <h2 className="text-4xl sm:text-5xl font-light text-white">
              Un service <span className="font-script text-rose-200">sur mesure</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={service.id}
                className="group p-8 rounded-2xl bg-gradient-to-br from-rose-900/10 to-transparent border border-rose-900/20 hover:border-rose-200/40 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-rose-200/10 border border-rose-200/30 text-rose-200 mb-6">
                  {icons[index].icon}
                </div>
                <h3 className="text-xl font-light text-rose-200 mb-4">{service.title}</h3>
                <p className="text-gray-400 leading-relaxed text-lg">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Interior Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0f0a0d]/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-rose-200 text-sm tracking-widest mb-4">À BORD</p>
            <h2 className="text-4xl sm:text-5xl font-light text-white">
              Un habitacle <span className="font-script text-rose-200">d'exception</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="rounded-2xl overflow-hidden group">
              <img
                src="/images/dashboard.jpg"
                alt="Tableau de bord et intérieur cuir"
                className="w-full h-[300px] object-cover group-hover:scale-110 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden group">
              <img
                src="/images/interior-rear.webp"
                alt="Sièges arrière en cuir spacieux"
                className="w-full h-[300px] object-cover group-hover:scale-110 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden group">
              <img
                src="/images/panoramic-roof.webp"
                alt="Toit panoramique spacieux"
                className="w-full h-[300px] object-cover group-hover:scale-110 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden group">
              <img
                src="/images/trunk.webp"
                alt="Coffre spacieux et pratique"
                className="w-full h-[300px] object-cover group-hover:scale-110 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - REFAIT SELON SITE DE RÉFÉRENCE */}
      <section id="tarifs" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0f0a0d]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-rose-200 text-sm tracking-widest mb-4">TARIFICATION</p>
            <h2 className="text-4xl sm:text-5xl font-light text-white">
              Des prix <span className="font-script text-rose-200">justes</span>
            </h2>
            <p className="text-gray-300 text-xl mt-6 leading-relaxed max-w-3xl mx-auto">
              Une tarification claire, sans surprise. Chaque course fait l'objet d'un devis personnalisé, 
              gratuit et sans engagement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Transfert */}
            <div className="relative p-8 rounded-2xl bg-gradient-to-br from-rose-900/10 to-transparent border border-rose-900/20 hover:border-rose-200/30 transition-all duration-300">
              <p className="text-rose-200 text-xs tracking-wider mb-2 uppercase">Aéroport & gare</p>
              <h3 className="text-2xl font-light text-white mb-4">Transfert</h3>
              <div className="mb-6">
                <span className="text-3xl font-light text-rose-200">Sur devis</span>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-rose-200 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400 text-base">Prise en charge à domicile</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-rose-200 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400 text-base">Suivi de vol / train inclus</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-rose-200 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400 text-base">Attente & bagages compris</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-rose-200 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400 text-base">Bouteille d'eau offerte</span>
                </li>
              </ul>
            </div>

            {/* Mise à disposition - POPULAIRE */}
            <div className="relative p-8 rounded-2xl bg-gradient-to-br from-rose-900/20 to-rose-800/10 border border-rose-200/40 hover:border-rose-200/60 transition-all duration-300">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="inline-block px-4 py-1 rounded-full bg-rose-200 text-gray-900 text-xs font-semibold uppercase">
                  Populaire
                </span>
              </div>
              <p className="text-rose-200 text-xs tracking-wider mb-2 uppercase">À l'heure</p>
              <h3 className="text-2xl font-light text-white mb-4">Mise à disposition</h3>
              <div className="mb-6">
                <span className="text-gray-400 text-sm mr-2">à partir de</span>
                <span className="text-3xl font-light text-rose-200">60€ / h</span>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-rose-200 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400 text-sm">Chauffeur dédié</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-rose-200 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400 text-sm">Trajets illimités</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-rose-200 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400 text-sm">Idéal shopping, RDV, événements</span>
                </li>
              </ul>
            </div>

            {/* Longue distance */}
            <div className="relative p-8 rounded-2xl bg-gradient-to-br from-rose-900/10 to-transparent border border-rose-900/20 hover:border-rose-200/30 transition-all duration-300">
              <p className="text-rose-200 text-xs tracking-wider mb-2 uppercase">Sur devis</p>
              <h3 className="text-2xl font-light text-white mb-4">Longue distance</h3>
              <div className="mb-6">
                <span className="text-gray-400 text-sm mr-2">à partir de</span>
                <span className="text-3xl font-light text-rose-200">1,50€ / km</span>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-rose-200 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400 text-sm">Trajets intervilles & Europe</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-rose-200 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400 text-sm">Confort premium</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-rose-200 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400 text-sm">Devis en 24h</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-rose-200 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400 text-sm">Paiement flexible</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-400 leading-relaxed mb-8 max-w-4xl mx-auto">
              Paiement par CB, espèces ou virement. Facture professionnelle disponible sur demande. 
              Les tarifs varient selon distance, horaires (majoration de nuit et jours fériés) et durée.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center space-x-3 px-10 py-4 rounded-full bg-rose-200 text-gray-900 hover:bg-rose-300 transition-all duration-300 transform hover:scale-105"
            >
              <span className="font-medium">Obtenir un devis</span>
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section - CARROUSEL MODERNE */}
      <section id="temoignages" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#0f0a0d] to-[#1a1014]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-rose-200 text-sm tracking-widest mb-4">ILS ME FONT CONFIANCE</p>
            <h2 className="text-4xl sm:text-5xl font-light text-white">
              Témoignages
            </h2>
            <div className="flex items-center justify-center space-x-2 mt-6">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={`star-rating-${i}`} className="w-6 h-6 text-rose-200 fill-current" />
                ))}
              </div>
              <span className="text-gray-300 text-lg">5.0 / 5</span>
            </div>
            <p className="text-gray-400 mt-2">— clientes vérifiées</p>
          </div>

          <TestimonialsCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0f0a0d]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-rose-200 text-sm tracking-widest mb-4">RÉSERVATION</p>
            <h2 className="text-4xl sm:text-5xl font-light text-white">
              Prenons <span className="font-script text-rose-200">contact</span>
            </h2>
            <p className="text-gray-300 text-lg mt-6 leading-relaxed max-w-3xl mx-auto">
              Réservez votre course ou demandez un devis. Je vous réponds personnellement dans les meilleurs délais.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <ContactInfo />
            <div className="lg:col-span-3">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OnePageHome;
